/**
 * №44 — THE COMMAND PALETTE, IN `/app`.
 *
 * David: *"a command palette is a DOING affordance and `/app` is where doing
 * happens; `/console` is admin and config."* The palette has existed since
 * Sprint 3 and has only ever been mounted in the console's layout
 * (`components/shell/AppShell.tsx:26,143`).
 *
 * ## Why this is a second component and not the same one moved
 *
 * The console's `CommandPalette` cannot render here, for three reasons that are
 * each fatal on their own (the step-0 audit, `Agent/spec-ladder/44-…md` §1):
 *
 *  1. its command sources call `useChatRail()` and `useRowDetailPanel()`, and
 *     **both throw** outside console providers this shell does not mount;
 *  2. its destinations are console routes — `/agents/:id`, `/library/:id`,
 *     `/workforce`, `/hire`, `/connections`, `/settings/members` — none of which
 *     exists under `/app`. A palette offering them is the dead-nav ban with a
 *     keyboard;
 *  3. it is styled in the console's token vocabulary (`--color-background-*`,
 *     `--kuvio-border-default`), not the App Shell's `--surface-*` / `--text-*`.
 *
 * So: two shells, **one ranking**. `rank.ts` is shared and was extracted for
 * exactly this — the chrome differs because the vocabularies differ; the maths
 * must not, or "why did it rank that differently over there" becomes a real
 * question about the product.
 *
 * ## What it offers
 *
 * Everything comes from what the shell already DECLARES — the sitemap, the
 * route→anchor matrix, `openRecordNav`, the roster (`app-commands.ts`). A
 * surface the shell does not declare cannot appear; one it does cannot be
 * forgotten.
 *
 * Lazy on open: the index (and its per-type row queries) mounts only once ⌘K has
 * been pressed for the first time, so a session that never opens it pays nothing.
 */
import { useMemo, useState } from "react";
import { Command as Cmdk } from "cmdk";
import { useAuth } from "../../../lib/hooks/use-auth";
import { useCommandPalette } from "../../components/command-palette/CommandPaletteContext";
import { loadRecency, recordCommandUse, selectRecentCommands } from "../../components/command-palette/commands/recent";
import { rankCommands } from "../../components/command-palette/rank";
import type { Command } from "../../components/command-palette/types";
import { useAppCommands } from "./use-app-commands";
import { restingGroups } from "./app-commands";

export function AppCommandPalette() {
  const { open } = useCommandPalette();
  // The latch: mount the (query-firing) index only once the palette has first
  // been opened, then keep it mounted so the caches stay warm. Render-phase
  // derived state — the guarded pattern, no open→setState effect.
  const [everOpened, setEverOpened] = useState(open);
  if (open && !everOpened) setEverOpened(true);
  if (!everOpened) return null;
  return <AppCommandPaletteInner />;
}

function AppCommandPaletteInner() {
  const { open, setOpen } = useCommandPalette();
  const { currentWorkspaceId } = useAuth();
  const [query, setQuery] = useState("");
  const { commands: all, places } = useAppCommands();

  const results = useMemo(
    () => rankCommands(query, all, loadRecency(currentWorkspaceId)),
    [query, all, currentWorkspaceId],
  );

  // The empty state is your own history, not a dump of the workspace — the
  // recency store is shared with the console palette and keyed per workspace,
  // and `app:`-prefixed ids mean the two sets can never collide in it.
  const recents = useMemo(
    () => selectRecentCommands(all, loadRecency(currentWorkspaceId), 6),
    [all, currentWorkspaceId],
  );

  // Clear the query on close, so reopening starts from the recents rather than
  // from whatever was typed last time. Render-phase derived state, NOT an effect:
  // React's own "storing information from previous renders" pattern, which
  // `AppShellLayout`'s nav drawer and this file's own `everOpened` latch already
  // use. `react-hooks/set-state-in-effect` rejects the effect form — and eslint
  // runs in no CI workflow, so it was `npx eslint` on the changed files that said
  // so, exactly as `console-conventions.md` warns.
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (!open && query) setQuery("");
  }

  function run(cmd: Command) {
    recordCommandUse(currentWorkspaceId, cmd.id);
    setOpen(false);
    cmd.execute();
  }

  // №53 (D-CR22) — the resting groups, in render order. `places` comes from the
  // shell context so the palette reads the SAME `section` mark the rail does.
  const resting = useMemo(
    () => restingGroups(all, places, recents),
    [all, places, recents],
  );

  // №48.2 — THE SELECTION IS CONTROLLED, and it has to be.
  //
  // `shouldFilter={false}`: this palette supplies its own ranked `results`, and
  // cmdk just renders them. Left uncontrolled, cmdk keeps the highlight where it
  // is when the list changes — so a record search that resolves a beat after the
  // keystroke lands BELOW a highlight already parked on the previous list's top
  // row. Type "Ellis", press Enter fast, and you open the WIP billing worksheet.
  //
  // CI caught it and I nearly mislabelled it flake: the assertion polled for ten
  // seconds and the selection never moved off `app:surface:wip-worksheet`, which
  // is not a race, it is a highlight that never follows the ranking.
  //
  // So the selection is derived from the FIRST RENDERED ROW, in render order
  // (results when searching; recents-then-here when not), and re-derived whenever
  // that row changes. Render-phase derived state, not an effect — the same
  // pattern as the `wasOpen` latch above, and for the same eslint reason.
  const rendered = query.trim() ? results : resting.flatMap((g) => g.commands);
  const firstId = rendered[0]?.id ?? "";
  const [selected, setSelected] = useState(firstId);
  const [lastFirstId, setLastFirstId] = useState(firstId);
  if (lastFirstId !== firstId) {
    setLastFirstId(firstId);
    setSelected(firstId);
  }

  if (!open) return null;

  // The empty state is RECENTS first, then a capped "Here".
  //
  // The first render led with "Here" and it was wrong in a way only the picture
  // showed (`docs/44-command-palette-evidence/palette-open-light.png`): a matter
  // declares TEN sub-views, so the context group filled the whole panel and
  // pushed your own recents out of sight — and those ten rows are already
  // permanently on screen in the nav's OPEN section, two inches to the left.
  // Recents are the thing you cannot see anywhere else, so they lead; "Here"
  // keeps a short head and the rest of it is one keystroke away. With no recents
  // yet, "Here" is at the top anyway, so a first-time palette still has something
  // to say.


  return (
    <div
      className="cmdk__scrim"
      data-testid="app-palette-scrim"
      onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
    >
      <div
        className="cmdk"
        role="dialog"
        aria-label="Command palette"
        data-testid="app-palette"
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            setOpen(false);
          }
        }}
      >
        <Cmdk shouldFilter={false} loop label="Command palette" value={selected} onValueChange={setSelected}>
          <div className="cmdk__inputrow">
            <i className="ti ti-search cmdk__searchicon" aria-hidden="true" />
            <Cmdk.Input
              value={query}
              onValueChange={setQuery}
              autoFocus
              placeholder="Go to a matter, a contact, a hire — or run a command"
              className="cmdk__input"
              data-testid="app-palette-input"
            />
          </div>

          <Cmdk.List className="cmdk__list">
            {query.trim() ? (
              <>
                <Cmdk.Empty className="cmdk__empty">Nothing matches “{query}”.</Cmdk.Empty>
                {results.map((cmd) => (
                  <Row key={cmd.id} cmd={cmd} onRun={run} />
                ))}
              </>
            ) : (
              <>
                {/* №53 (D-CR22) — the POPULATED resting state. The `Here` group is
                    folded into `Go to`: with the rail down to six rows there is no
                    longer a permanently-visible OPEN section two inches to the left
                    duplicating it, which was the whole reason it was capped and
                    demoted in №44. See `restingGroups` for what each group is and
                    why none of it is hand-kept. */}
                {resting.map((g) => (
                  <Cmdk.Group key={g.key} heading={g.heading} className="cmdk__group">
                    {g.commands.map((cmd) => (
                      <Row key={cmd.id} cmd={cmd} onRun={run} />
                    ))}
                  </Cmdk.Group>
                ))}
                {/* Still honest when there is genuinely nothing: a brand-new
                    workspace with no modules installed has no Places, no records
                    and no history, and inventing rows for it would be the exact
                    thing the dead-nav ban forbids. */}
                {resting.length === 0 && (
                  <div className="cmdk__empty">Type to search matters, contacts, hires and places.</div>
                )}
              </>
            )}
          </Cmdk.List>

          <div className="cmdk__foot">
            <span><kbd>↑</kbd><kbd>↓</kbd> move</span>
            <span><kbd>↵</kbd> go</span>
            <span><kbd>esc</kbd> close</span>
          </div>
        </Cmdk>
      </div>
    </div>
  );
}

function Row({ cmd, onRun }: { cmd: Command; onRun: (c: Command) => void }) {
  return (
    <Cmdk.Item value={cmd.id} onSelect={() => onRun(cmd)} className="cmdk__item">
      <i className={`ti ti-${cmd.icon ?? "command"} cmdk__icon`} aria-hidden="true" />
      <span className="cmdk__label">{cmd.label}</span>
      {cmd.subtitle && <span className="cmdk__sub">{cmd.subtitle}</span>}
    </Cmdk.Item>
  );
}
